/**
 * Roles Page - 권한 관리
 * 
 * 시스템 권한(Role) 목록 조회, 생성, 삭제
 */

import { useState, useEffect } from 'react';
import { Topbar, PanelTitle } from '../components/layout';
import { Button, Modal, SearchInput, SortableTh, useTableSort, useToast } from '../components/ui';
import { roleApi, Role, CreateRoleInput } from '../api/roles';
import EditIcon from '../assets/icons/common/edit.svg?react';
import TrashIcon from '../assets/icons/common/trash.svg?react';
import '../styles/adminPage.css';

interface Props {
  embedded?: boolean;
}

export function RolesPage({ embedded = false }: Props) {
  const { showToast } = useToast();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const sort = useTableSort<'roleId' | 'roleName' | 'description' | 'sortOrder' | 'createdAt'>();

  // 생성 모달
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState<CreateRoleInput>({ roleId: '', roleName: '', description: '', sortOrder: undefined });
  const [addErrors, setAddErrors] = useState<Set<string>>(new Set());

  // 상세/수정 모달
  const [selected, setSelected] = useState<Role | null>(null);
  const [editForm, setEditForm] = useState({ roleName: '', description: '', sortOrder: undefined as number | undefined });
  const [saving, setSaving] = useState(false);

  // 삭제 확인 모달
  const [deleteConfirm, setDeleteConfirm] = useState<{ roleId: string; roleName: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  /** 권한 목록 로드 */
  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await roleApi.list();
      setRoles(data);
    } catch (err) {
      showToast('error', '권한 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  // 검색 필터링
  const filtered = roles.filter((r) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (r.roleId || '').toLowerCase().includes(q) ||
      (r.roleName || '').toLowerCase().includes(q) ||
      (r.description || '').toLowerCase().includes(q)
    );
  });

  // 검색어 하이라이트
  const highlightText = (text: string) => {
    if (!searchQuery.trim() || !text) return text;
    const q = searchQuery.toLowerCase();
    const idx = text.toLowerCase().indexOf(q);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="search-highlight">{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </>
    );
  };

  // 에러 클리어
  const clearAddError = (field: string) => {
    setAddErrors((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };

  // 등록 모달 닫기
  const closeAddModal = () => {
    setShowCreateModal(false);
    setNewRole({ roleId: '', roleName: '', description: '', sortOrder: undefined });
    setAddErrors(new Set());
  };

  /** 권한 생성 */
  const handleCreate = async () => {
    const errs = new Set<string>();
    if (!newRole.roleId.trim()) errs.add('roleId');
    if (!newRole.roleName.trim()) errs.add('roleName');
    if (errs.size > 0) { setAddErrors(errs); return; }
    setAddErrors(new Set());

    try {
      setCreating(true);
      await roleApi.create(newRole);
      showToast('success', '권한이 생성되었습니다.');
      closeAddModal();
      loadRoles();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || '권한 생성에 실패했습니다.');
    } finally {
      setCreating(false);
    }
  };

  // 상세 모달 열기
  const openDetail = (r: Role) => {
    setSelected(r);
    setEditForm({ roleName: r.roleName, description: r.description || '', sortOrder: r.sortOrder });
  };

  // 상세 모달 닫기
  const closeDetail = () => {
    setSelected(null);
    setEditForm({ roleName: '', description: '', sortOrder: undefined });
  };

  /** 권한 수정 */
  const handleSave = async () => {
    if (!selected) return;
    if (!editForm.roleName.trim()) {
      showToast('error', '권한 이름은 필수입니다.');
      return;
    }
    try {
      setSaving(true);
      await roleApi.update(selected.roleId, editForm);
      setRoles((prev) => prev.map((r) => (r.roleId === selected.roleId ? { ...r, ...editForm } : r)));
      showToast('success', '권한 정보가 수정되었습니다.');
      closeDetail();
    } catch {
      showToast('error', '수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  /** 권한 삭제 */
  const handleDelete = async (roleId: string, roleName: string) => {
    setDeleteConfirm({ roleId, roleName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(true);
      await roleApi.delete(deleteConfirm.roleId);
      setRoles((prev) => prev.filter((r) => r.roleId !== deleteConfirm.roleId));
      showToast('success', '권한이 삭제되었습니다.');
    } catch (err: any) {
      showToast('error', err.response?.data?.error || '삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  // 검색바
  const searchBar = (
    <div className="users-search-bar">
      <SearchInput
        placeholder="ID, 이름으로 검색..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onClear={() => setSearchQuery('')}
      />
      <button className="users-search-refresh" onClick={loadRoles} disabled={loading} title="새로고침">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
          <path d="M21 3v5h-5" />
        </svg>
      </button>
    </div>
  );

  return (
    <>
      {!embedded && (
        <Topbar title="권한 관리" description="시스템 권한을 관리합니다" />
      )}

      <div className={embedded ? '' : 'page-content'}>
        {embedded && (
          <div className="admin-panel-head">
            <PanelTitle title="권한 관리" description="시스템 권한을 관리합니다." />
            <div className="admin-panel-actions">
              {roles.length > 0 && searchBar}
              <Button color="primary" onClick={() => setShowCreateModal(true)}>+ 권한 등록</Button>
            </div>
          </div>
        )}
        <div className={embedded ? 'admin-section' : 'page-content-inner'}>
          {!embedded && (
            <div className="admin-panel-actions" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
              {roles.length > 0 && searchBar}
              <Button color="primary" onClick={() => setShowCreateModal(true)}>+ 권한 등록</Button>
            </div>
          )}

          {loading ? (
            <div className="loading">로딩 중...</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th><SortableTh sort={sort} col="roleId">권한 ID</SortableTh></th>
                    <th><SortableTh sort={sort} col="roleName">권한 이름</SortableTh></th>
                    <th><SortableTh sort={sort} col="description">설명</SortableTh></th>
                    <th><SortableTh sort={sort} col="sortOrder">순서</SortableTh></th>
                    <th><SortableTh sort={sort} col="createdAt">생성일</SortableTh></th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="table-empty">
                        {searchQuery ? `"${searchQuery}"에 대한 검색 결과가 없습니다.` : '등록된 권한이 없습니다. 우측 상단 "권한 등록"으로 추가하세요.'}
                      </td>
                    </tr>
                  )}
                  {sort.sortRows(filtered, (r, key) => {
                    if (key === 'sortOrder') return r.sortOrder ?? 9999;
                    return r[key] || '';
                  }).map((role) => (
                    <tr key={role.roleId}>
                      <td className="left">
                        <a href="#" className="table-link" onClick={(e) => { e.preventDefault(); openDetail(role); }}>
                          {highlightText(role.roleId)}
                        </a>
                      </td>
                      <td className="left">{highlightText(role.roleName)}</td>
                      <td>{role.description || '-'}</td>
                      <td>{role.sortOrder ?? '-'}</td>
                      <td>{role.createdAt ? new Date(role.createdAt).toLocaleDateString() : '-'}</td>
                      <td>
                        <div className="user-actions">
                          <button className="icon-action-btn" onClick={() => openDetail(role)} title="수정"><EditIcon /></button>
                          <button className="icon-action-btn delete" onClick={() => handleDelete(role.roleId, role.roleName)} title="삭제"><TrashIcon /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 상세/수정 모달 */}
      <Modal
        isOpen={!!selected}
        onClose={closeDetail}
        title="권한 상세 정보"
        size="sm"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeDetail}>취소</Button>
            <Button color="primary" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</Button>
          </>
        }
      >
        {selected && (
          <div className="form-grid">
            <div className="form-item">
              <div className="form-label">권한 ID</div>
              <div className="input">
                <input className="input-control" type="text" value={selected.roleId} disabled />
              </div>
            </div>
            <div className="form-item">
              <div className="form-label">권한 이름</div>
              <div className="input">
                <input className="input-control" type="text" value={editForm.roleName}
                  onChange={(e) => setEditForm({ ...editForm, roleName: e.target.value })} />
              </div>
            </div>
            <div className="form-item">
              <div className="form-label">설명</div>
              <div className="input">
                <input className="input-control" type="text" value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
              </div>
            </div>
            <div className="form-item">
              <div className="form-label">정렬 순서</div>
              <div className="input">
                <input className="input-control" type="number" placeholder="1, 2, 3..."
                  value={editForm.sortOrder ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, sortOrder: e.target.value ? Number(e.target.value) : undefined })} />
              </div>
            </div>
            <p className="user-detail-meta">
              생성일: {selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '-'}
            </p>
          </div>
        )}
      </Modal>

      {/* 등록 모달 */}
      <Modal
        isOpen={showCreateModal}
        onClose={closeAddModal}
        title="권한 등록"
        size="sm"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeAddModal}>취소</Button>
            <Button color="primary" onClick={handleCreate} disabled={creating}>{creating ? '등록 중...' : '등록'}</Button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-item">
            <div className="form-label">권한 ID <span className="required">*</span></div>
            <div className={`input${addErrors.has('roleId') ? ' has-error' : ''}`}>
              <input className="input-control" type="text" placeholder="예: marketingAdmin"
                value={newRole.roleId} onChange={(e) => { setNewRole({ ...newRole, roleId: e.target.value }); clearAddError('roleId'); }} />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">권한 이름 <span className="required">*</span></div>
            <div className={`input${addErrors.has('roleName') ? ' has-error' : ''}`}>
              <input className="input-control" type="text" placeholder="예: 마케팅 관리자"
                value={newRole.roleName} onChange={(e) => { setNewRole({ ...newRole, roleName: e.target.value }); clearAddError('roleName'); }} />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">설명</div>
            <div className="input">
              <input className="input-control" type="text" placeholder="권한에 대한 설명 (선택)"
                value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">정렬 순서</div>
            <div className="input">
              <input className="input-control" type="number" placeholder="1, 2, 3..."
                value={newRole.sortOrder ?? ''} onChange={(e) => setNewRole({ ...newRole, sortOrder: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
          </div>
        </div>
      </Modal>

      {/* 권한 삭제 확인 모달 */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="권한 삭제"
        size="sm"
        style={{ maxWidth: 360 }}
        bodyClassName="dialog-body--compact"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => setDeleteConfirm(null)}>취소</Button>
            <Button color="error" onClick={confirmDelete} disabled={deleting}>삭제</Button>
          </>
        }
      >
        <p style={{ margin: '4px 0 12px', lineHeight: 1.6, textAlign: 'center' }}>
          "{deleteConfirm?.roleName}" 권한을 삭제하시겠습니까?<br />삭제 후 복구할 수 없습니다.
        </p>
      </Modal>
    </>
  );
}

export default RolesPage;
